import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, Trash2, Ban, AlertTriangle } from 'lucide-react';
import { toast } from '@/hooks/use-toast';

interface ConfirmDeleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: string;
  itemName?: string;
  itemType?: string;
  onConfirm: () => Promise<void> | void;
  isLoading?: boolean;
  actionText?: string;
  actionType?: 'delete' | 'revoke' | 'warning';
  successMessage?: string;
}

const ConfirmDeleteDialog: React.FC<ConfirmDeleteDialogProps> = ({
  open,
  onOpenChange,
  title,
  description,
  itemName,
  itemType = 'item',
  onConfirm,
  isLoading: externalLoading,
  actionText,
  actionType = 'delete',
  successMessage,
}) => {
  const [internalLoading, setInternalLoading] = useState(false);
  const isLoading = externalLoading ?? internalLoading;

  const getIcon = () => {
    switch (actionType) {
      case 'revoke':
        return <Ban className='h-6 w-6 text-red-600' />;
      case 'warning':
        return <AlertTriangle className='h-6 w-6 text-amber-600' />;
      default:
        return <Trash2 className='h-6 w-6 text-red-600' />;
    }
  };

  const getButtonIcon = () => {
    switch (actionType) {
      case 'revoke':
        return <Ban className='h-4 w-4' />;
      case 'warning':
        return <AlertTriangle className='h-4 w-4' />;
      default:
        return <Trash2 className='h-4 w-4' />;
    }
  };

  const getDefaultActionText = () => {
    switch (actionType) {
      case 'revoke':
        return 'Revoke';
      case 'warning':
        return 'Confirm';
      default:
        return 'Delete';
    }
  };

  const getLoadingText = () => {
    switch (actionType) {
      case 'revoke':
        return 'Revoking...';
      case 'warning':
        return 'Processing...';
      default:
        return 'Deleting...';
    }
  };

  const handleConfirm = async () => {
    try {
      setInternalLoading(true);
      await onConfirm();
      toast({
        title: 'Success',
        description: successMessage || `${itemType} ${actionType === 'revoke' ? 'revoked' : 'deleted'} successfully`,
        variant: 'success',
      });
      onOpenChange(false);
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error && error.message
          ? error.message
          : `Failed to ${actionType === 'revoke' ? 'revoke' : 'delete'} ${itemType}. Please try again.`;

      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setInternalLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className='sm:max-w-[440px]'>
        <DialogHeader>
          <div className='flex items-center space-x-3'>
            <div
              className={`flex h-12 w-12 items-center justify-center rounded-full ${
                actionType === 'warning' ? 'bg-amber-500/10' : 'bg-red-500/10'
              }`}
            >
              {getIcon()}
            </div>
            <div className='flex-1'>
              <DialogTitle className='text-[1.05rem] font-semibold'>
                {title || `Delete ${itemType}`}
              </DialogTitle>
              <DialogDescription className='mt-0 text-[0.8rem] text-slate-500'>
                {actionType === 'revoke' ? 'This action cannot be undone' : 'Permanently remove this item'}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className='py-4'>
          <p className='text-sm text-slate-700'>
            {description ||
              `Are you sure you want to ${actionType === 'revoke' ? 'revoke' : 'delete'} ${
                itemName ? `"${itemName}"` : `this ${itemType}`
              }?`}
          </p>
          {!description && (
            <p className='mt-2 text-sm text-slate-500'>This action cannot be undone.</p>
          )}
        </div>

        <DialogFooter>
          <Button type='button' variant='outline' onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isLoading}
            className={
              actionType === 'warning'
                ? 'bg-amber-600 hover:bg-amber-700'
                : 'bg-red-600 hover:bg-red-700'
            }
          >
            {isLoading ? (
              <>
                <Loader2 className='h-4 w-4 animate-spin' />
                {getLoadingText()}
              </>
            ) : (
              <>
                {getButtonIcon()}
                {actionText || getDefaultActionText()}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmDeleteDialog;
